// components/CompatibilityResults.jsx
import React, { useState } from "react";
import { FaInfoCircle, FaChartLine } from "react-icons/fa";
import { convertSnakeCaseInText, toNormalText } from "../utils/helpers";
import TrajectoryGraphPopup from '../Trajectory/TrajectoryGraphPopup';

const WindowTabs = ({ selectedWindow, setSelectedWindow }) => {
  const tabs = [
    { key: "latest", label: "Latest" },
    { key: "7d", label: "7 Days" },
    { key: "30d", label: "30 Days" },
    { key: "90d", label: "90 Days" },
  ];

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      {tabs.map((t) => {
        const active = selectedWindow === t.key;
        return (
          <button
            key={t.key}
            onClick={() => setSelectedWindow(t.key)}
            className="toggle-btn"
            style={{
              borderRadius: 999,
              padding: "6px 12px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: active ? "rgba(255,255,255,0.10)" : "transparent",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

const LatestResult = ({ liveResult, sharedStateMeaning, meetingsToDisplay, onViewGraph, graphDataAvailable }) => {
  if (!liveResult?.result) {
    return null
  }

  return (
    <>
      <div className="score-row">
        <div className="score-card">
          <p className="score-title">Synchrony</p>
          <p className="score-value">
            {Math.round((liveResult.result.biometric_synchrony_score || 0) * 100)}%
          </p>
        </div>

        <div className="score-card">
          <p className="score-title">Resonance</p>
          <p className="score-value">
            {liveResult.result.meeting_resonance_score ?? 0}%
          </p>
        </div>

        <div className="score-card">
          <p className="score-title">Overall</p>
          <p className="score-value">
            {Math.round((liveResult.result.overall_score || 0) * 100)}%
          </p>
        </div>

        <div className="score-card">
          <p className="score-title">Data Quality</p>
          <p className="score-value">
            {toNormalText(liveResult.result.data_quality)}
          </p>
        </div>
      </div>

      <div className="state-box">
        <div className="state-box-header">
          <p>
            <b>Meeting:</b> #{meetingsToDisplay.length}{" "}
            <span className={`badge ${liveResult.source || "none"}`}>
              {liveResult.source || "none"}
            </span>
          </p>
          {/* {graphDataAvailable && (
            <button 
              className="view-graph-btn-small" 
              onClick={onViewGraph}
              title="View detailed graph and insights"
            >
              <FaChartLine /> Graph
            </button>
          )} */}
        </div>
        <p>
          <b>Shared State:</b> {toNormalText(liveResult.result.shared_state)}
        </p>
        <p className="state-meaning">
          {sharedStateMeaning(liveResult.result.shared_state)}
        </p>
        <p>
          <b>Best Alignment Lag:</b> {liveResult.result.best_alignment_lag_sec ?? 0} sec
        </p>
      </div>

      <div className="details-box">
        <h4>Insights</h4>
        {Array.isArray(liveResult.result.insights) && liveResult.result.insights.length ? (
          <ul className="insights-list">
            {liveResult.result.insights.map((t, i) => (
              <li key={i}>{convertSnakeCaseInText(t)}</li>
            ))}
          </ul>
        ) : (
          <div className="muted">No insights available.</div>
        )}
      </div>
    </>
  );
};

const RelationshipTrajectorySummary = ({ 
  relationshipTrajectory, 
  user, 
  selectedUser, 
  selectedWindow, 
  getWindowLabel, 
  trendMeaning, 
  sharedStateMeaning, 
  trajectoryLoading,
  onViewGraph 
}) => {
  const windowLabel = getWindowLabel(selectedWindow);

  if (trajectoryLoading) return <p className="live-waiting">Loading trajectory...</p>;

  const meetingsCount = relationshipTrajectory?.coverage?.meetings_count ?? 0;

  if (!relationshipTrajectory || meetingsCount === 0) {
    return (
      <div className="details-box">
        <h4>Relationship Trajectory</h4>
        <div className="muted">No meetings available in the last {windowLabel}.</div>
      </div>
    );
  }

  const traj = relationshipTrajectory.trajectory || {};
  const coverage = relationshipTrajectory.coverage || {};
  const notes = relationshipTrajectory.notes || [];
  const strengths = relationshipTrajectory.strength_patterns || [];
  const frictions = relationshipTrajectory.friction_patterns || [];

  return (
    <div className="details-box">
      <div className="trajectory-header">
        <h4>
          Relationship Trajectory: {user?.first_name || "You"} +{" "}
          {selectedUser?.first_name || "Friend"}
        </h4>
        <button 
          className="view-graph-btn" 
          onClick={onViewGraph}
          title="View detailed graph and insights"
        >
          <FaChartLine /> View Graph
        </button>
      </div>

      <div className="score-row" style={{ marginTop: 12 }}>
        <div className="score-card">
          <p className="score-title">Regulation</p>
          <p className="score-value" style={{ fontSize: 18 }}>
            {traj.regulation || "insufficient_data"}
          </p>
          <div className="muted">{trendMeaning(traj.regulation)}</div>
        </div>

        <div className="score-card">
          <p className="score-title">Repair</p>
          <p className="score-value" style={{ fontSize: 18 }}>
            {traj.repair || "insufficient_data"}
          </p>
          <div className="muted">{trendMeaning(traj.repair)}</div>
        </div>

        <div className="score-card">
          <p className="score-title">Integration</p>
          <p className="score-value" style={{ fontSize: 18 }}>
            {traj.integration || "insufficient_data"}
          </p>
          <div className="muted">{trendMeaning(traj.integration)}</div>
        </div>
      </div>

      <div className="state-box" style={{ marginTop: 12 }}>
        <p><b>Window:</b> {relationshipTrajectory.window || selectedWindow}</p>
        <p><b>Range:</b> {relationshipTrajectory.range?.start || "-"} → {relationshipTrajectory.range?.end || "-"}</p>
        <p><b>Meetings Count:</b> {coverage.meetings_count ?? 0}</p>
        <p><b>Days With Baseline:</b> {coverage.days_with_baseline ?? 0}</p>
        <p><b>Data Quality:</b> {toNormalText(coverage.data_quality) || "-"}</p>
      </div>

      {Array.isArray(notes) && notes.length > 0 && (
        <div className="details-box inner">
          <h4>Notes</h4>
          <ul className="insights-list">
            {notes.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(strengths) && strengths.length > 0 && (
        <div className="details-box inner">
          <h4>Strength Patterns</h4>
          <ul className="insights-list">
            {strengths.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(frictions) && frictions.length > 0 && (
        <div className="details-box inner">
          <h4>Friction Patterns</h4>
          <ul className="insights-list">
            {frictions.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// MeetingDetails component - Updated with design-matching graph icon

const MeetingDetails = ({ 
  meeting, 
  index, 
  totalMeetings, 
  isOpen, 
  toggle, 
  sharedStateMeaning, 
  convertSnakeCaseInText, 
  toNormalText,
  onViewGraph,
  graphDataAvailable 
}) => {
  const hasResult = !!(meeting.result || meeting.synchrony_score);
  const meetingId = meeting.meeting_id || meeting.meetingId || index;
  
  const synchronyScore = meeting.result?.biometric_synchrony_score ?? meeting.synchrony_score ?? 0;
  const resonanceScore = meeting.result?.meeting_resonance_score ?? meeting.resonance_score ?? 0;
  const overallScore = meeting.result?.overall_score ?? meeting.overall_score ?? 0;
  const sharedState = meeting.result?.shared_state || meeting.shared_state || "";
  const dataQuality = meeting.result?.data_quality || meeting.data_quality || "";
  const insights = meeting.result?.insights || meeting.insights || [];
  const bestAlignmentLag = meeting.result?.best_alignment_lag_sec ?? meeting.best_alignment_lag_sec ?? 0;
  const breakdown = meeting.result?.breakdown || meeting.breakdown || {};
  const modalityBreakdown = meeting.result?.modality_breakdown || meeting.modality_breakdown || {};
  const source = meeting.source || "unknown";
  const updatedAt = meeting.updatedAt || meeting.updated_at || null;

  return (
    <div className={`meeting-card ${hasResult ? "has-result" : "no-result"}`}>
      <div className="meeting-card-top">
        <div className="meeting-title">
          <div className="meeting-id">Meeting #{totalMeetings - index}</div>
          <div className={`badge ${source}`}>{source}</div>
          {graphDataAvailable && (
            <button 
              className="graph-icon-btn" 
              onClick={() => onViewGraph(meeting)}
              title="View graph for this meeting"
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
            </button>
          )}
        </div>
        <button className="toggle-btn" onClick={toggle}>
          {isOpen ? "Hide" : "View"}
        </button>
      </div>

      {hasResult ? (
        <div className="meeting-summary">
          <div className="mini-stat">
            <span className="mini-label">Synchrony</span>
            <span className="mini-value">{Math.round(synchronyScore * 100)}%</span>
          </div>
          <div className="mini-stat">
            <span className="mini-label">Resonance</span>
            <span className="mini-value">{resonanceScore}%</span>
          </div>
          <div className="mini-stat">
            <span className="mini-label">Overall</span>
            <span className="mini-value">{Math.round(overallScore * 100)}%</span>
          </div>
          <div className="mini-stat">
            <span className="mini-label">State</span>
            <span className="mini-value">{toNormalText(sharedState)}</span>
          </div>
          <div className="mini-stat">
            <span className="mini-label">Quality</span>
            <span className="mini-value">{toNormalText(dataQuality)}</span>
          </div>
        </div>
      ) : (
        <div className="meeting-empty">No result computed for this meeting.</div>
      )}

      {isOpen && (
        <div className="meeting-details">
          {hasResult ? (
            <>
              <div className="details-row">
                <div><b>Best Alignment Lag:</b> {bestAlignmentLag} sec</div>
                <div className="updated-at">
                  <b>Updated:</b> {updatedAt ? new Date(updatedAt).toLocaleString() : "-"}
                </div>
              </div>

              <div className="details-box inner">
                <h4>Wearable Breakdown</h4>
                <div className="breakdown-grid">
                  <div className="breakdown-item">
                    <span>HR Sync</span>
                    <b>{Math.round((breakdown.hr_sync || 0) * 100)}%</b>
                  </div>
                  <div className="breakdown-item">
                    <span>RR Sync</span>
                    <b>{Math.round((breakdown.rr_sync || 0) * 100)}%</b>
                  </div>
                  <div className="breakdown-item">
                    <span>HRV Sync</span>
                    <b>{Math.round((breakdown.hrv_sync || 0) * 100)}%</b>
                  </div>
                  <div className="breakdown-item">
                    <span>Skin Temp</span>
                    <b>{Math.round((breakdown.skin_temp_sync || 0) * 100)}%</b>
                  </div>
                  <div className="breakdown-item">
                    <span>Movement</span>
                    <b>{Math.round((breakdown.micro_movement_sync || 0) * 100)}%</b>
                  </div>
                  <div className="breakdown-item">
                    <span>Coverage</span>
                    <b>{Math.round((breakdown.coverage || 0) * 100)}%</b>
                  </div>
                </div>
              </div>

              {(modalityBreakdown.voice_score != null || breakdown.pitch_sync != null) && (
                <div className="details-box inner">
                  <h4>Voice Score</h4>
                  <div className="state-box" style={{ marginBottom: 12 }}>
                    <p><b>Voice Overall:</b> {modalityBreakdown.voice_score != null ? `${Math.round(modalityBreakdown.voice_score * 100)}%` : "-"}</p>
                    <p className="muted">Based on: Pitch variability, Speech rate, Pauses, Vocal tremor, Warmth resonance, Interrupt patterns</p>
                  </div>
                  <div className="breakdown-grid">
                    <div className="breakdown-item"><span>Pitch Variability</span><b>{Math.round((breakdown.pitch_sync || 0) * 100)}%</b></div>
                    <div className="breakdown-item"><span>Speech Rate</span><b>{Math.round((breakdown.speech_rate_sync || 0) * 100)}%</b></div>
                    <div className="breakdown-item"><span>Pauses</span><b>{Math.round((breakdown.pause_score || 0) * 100)}%</b></div>
                    <div className="breakdown-item"><span>Vocal Tremor</span><b>{Math.round((breakdown.tremor_score || 0) * 100)}%</b></div>
                    <div className="breakdown-item"><span>Warmth Resonance</span><b>{Math.round((breakdown.warmth_score || 0) * 100)}%</b></div>
                    <div className="breakdown-item"><span>Interrupt Patterns</span><b>{Math.round((breakdown.interrupt_score || 0) * 100)}%</b></div>
                  </div>
                </div>
              )}
              {(modalityBreakdown.camera_score != null || breakdown.eye_score != null) && (
                <div className="details-box inner">
                  <h4>Camera Score</h4>
                  <div className="state-box" style={{ marginBottom: 12 }}>
                    <p>
                      <b>Camera Overall:</b>{" "}
                      {modalityBreakdown.camera_score != null
                        ? `${Math.round(modalityBreakdown.camera_score * 100)}%`
                        : "-"}
                    </p>
                    <p className="muted">
                      Based on: Eye contact, Blink rate, Facial tension, Smile authenticity,
                      Head tilt openness, Lean posture, Micro joy, Micro fear, Micro disgust,
                      Micro surprise
                    </p>
                  </div>
                  <div className="breakdown-grid">
                    <div className="breakdown-item">
                      <span>Eye Contact</span>
                      <b>{Math.round((breakdown.eye_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Blink Rate</span>
                      <b>{Math.round((breakdown.blink_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Facial Tension</span>
                      <b>{Math.round((breakdown.tension_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Smile Authenticity</span>
                      <b>{Math.round((breakdown.smile_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Head Tilt</span>
                      <b>{Math.round((breakdown.head_tilt_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Lean Posture</span>
                      <b>{Math.round((breakdown.lean_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Micro Joy</span>
                      <b>{Math.round((breakdown.joy_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Micro Fear</span>
                      <b>{Math.round((breakdown.fear_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Micro Disgust</span>
                      <b>{Math.round((breakdown.disgust_score || 0) * 100)}%</b>
                    </div>
                    <div className="breakdown-item">
                      <span>Micro Surprise</span>
                      <b>{Math.round((breakdown.surprise_score || 0) * 100)}%</b>
                    </div>
                  </div>
                </div>
              )}

              <div className="details-box inner">
                <h4>Insights</h4>
                {Array.isArray(insights) && insights.length ? (
                  <ul className="insights-list">
                    {insights.map((txt, idx) => <li key={idx}>{convertSnakeCaseInText(txt)}</li>)}
                  </ul>
                ) : (
                  <div className="muted">No insights available.</div>
                )}
              </div>

              <div className="muted">{sharedStateMeaning(sharedState)}</div>
            </>
          ) : (
            <div className="muted">This meeting has no computed result yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

const CompatibilityResults = ({ 
  selectedUser, 
  liveResult, 
  allMeetingsData, 
  relationshipTrajectory,
  selectedWindow, 
  setSelectedWindow,
  isLoading,
  user,
  getWindowLabel,
  trendMeaning,
  sharedStateMeaning,
  convertSnakeCaseInText,
  toNormalText,
  setShowToolTipPopup,
  trajectoryLoading
}) => {
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);
  const [showGraphPopup, setShowGraphPopup] = useState(false);
  const [selectedMeetingForGraph, setSelectedMeetingForGraph] = useState(null);

  const getMeetingsToDisplay = () => {
    if (selectedWindow === "latest") {
      return Array.isArray(allMeetingsData?.meetings)
        ? [...allMeetingsData.meetings].sort((a, b) => {
            const ar = a.result ? 1 : 0;
            const br = b.result ? 1 : 0;
            if (br !== ar) return br - ar;
            return Number(b.meeting_id) - Number(a.meeting_id);
          })
        : [];
    }
    return relationshipTrajectory?.meetings
      ? [...relationshipTrajectory.meetings].sort((a, b) => Number(b.meeting_id) - Number(a.meeting_id))
      : [];
  };

  const meetingsToDisplay = getMeetingsToDisplay();
  
  // FIX: Check if there are ANY meetings with results, from either source
  const hasAnyMeetingData = meetingsToDisplay.length > 0 && meetingsToDisplay.some(m => m.result || m.synchrony_score);
  
  // For graph data, we can use either relationshipTrajectory or build from allMeetingsData
  const graphDataAvailable = hasAnyMeetingData && (
    (relationshipTrajectory && relationshipTrajectory.meetings?.length > 0) ||
    (allMeetingsData && allMeetingsData.meetings?.length > 0)
  );

  const handleViewGraph = (meeting = null) => {
    setSelectedMeetingForGraph(meeting);
    setShowGraphPopup(true);
  };

  if (!selectedUser?.user_id) return null;

  // Build graph data from available meetings if relationshipTrajectory is empty
  const getGraphData = () => {
    if (relationshipTrajectory && relationshipTrajectory.meetings?.length > 0) {
      return relationshipTrajectory;
    }
    
    // If no relationshipTrajectory, build from allMeetingsData
    if (allMeetingsData && allMeetingsData.meetings?.length > 0) {
      return {
        meetings: allMeetingsData.meetings,
        trajectory: {
          regulation: 'insufficient_data',
          repair: 'insufficient_data',
          integration: 'insufficient_data'
        },
        strength_patterns: [],
        friction_patterns: [],
        notes: ['Graph data built from meeting history.']
      };
    }
    
    return null;
  };

  const graphData = getGraphData();

  return (
    <>
      <div className="live-result-box">
        <div className="live-result-header">
          <div className="w-100">
            <div className="d-flex align-items-center justify-content-between w-100">
              <h3>Compatibility Results</h3>
              <button
                type="button"
                className="border-0 bg-transparent text-light mb-3"
                onClick={() => setShowToolTipPopup(true)}
              >
                <FaInfoCircle />
              </button>
            </div>
            <WindowTabs selectedWindow={selectedWindow} setSelectedWindow={setSelectedWindow} />
          </div>
          {isLoading ? <span className="live-loading">Updating...</span> : null}
        </div>

        {!meetingsToDisplay.length ? (
          <p className="live-waiting">
            {selectedWindow === "latest"
              ? "No meetings found between these users."
              : `No meetings found in the last ${getWindowLabel(selectedWindow)}.`}
          </p>
        ) : (
          <>
            {selectedWindow === "latest" ? (
              <LatestResult 
                liveResult={liveResult} 
                sharedStateMeaning={sharedStateMeaning} 
                meetingsToDisplay={meetingsToDisplay}
                onViewGraph={() => handleViewGraph(null)}
                graphDataAvailable={graphDataAvailable}
              />
            ) : (
              <RelationshipTrajectorySummary
                relationshipTrajectory={relationshipTrajectory}
                user={user}
                selectedUser={selectedUser}
                selectedWindow={selectedWindow}
                getWindowLabel={getWindowLabel}
                trendMeaning={trendMeaning}
                sharedStateMeaning={sharedStateMeaning}
                trajectoryLoading={trajectoryLoading}
                onViewGraph={() => handleViewGraph(null)}
              />
            )}

            <div className="details-box">
              <h4>
                {selectedWindow === "latest"
                  ? "All Meetings"
                  : `Meetings (Last ${getWindowLabel(selectedWindow)})`}
              </h4>

              <div className="meetings-grid">
                {meetingsToDisplay.map((m, i) => (
                  <MeetingDetails
                    key={m.meeting_id || m.meetingId || i}
                    meeting={m}
                    index={i}
                    totalMeetings={meetingsToDisplay.length}
                    isOpen={expandedMeetingId === (m.meeting_id || m.meetingId || i)}
                    toggle={() => setExpandedMeetingId(
                      expandedMeetingId === (m.meeting_id || m.meetingId || i) 
                        ? null 
                        : (m.meeting_id || m.meetingId || i)
                    )}
                    sharedStateMeaning={sharedStateMeaning}
                    convertSnakeCaseInText={convertSnakeCaseInText}
                    toNormalText={toNormalText}
                    onViewGraph={handleViewGraph}
                    graphDataAvailable={graphDataAvailable}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Graph Popup */}
      {showGraphPopup && graphData && (
        <TrajectoryGraphPopup
          data={graphData}
          onClose={() => setShowGraphPopup(false)}
          user={user}
          selectedUser={selectedUser}
          highlightMeeting={selectedMeetingForGraph}
        />
      )}
    </>
  );
};

export default CompatibilityResults;